import React, { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer,
  Tooltip, ReferenceLine,
} from "recharts";
import { Droplets, BookOpen } from "lucide-react";
import { useI18n } from "@/lib/i18n";

// Rockwool WR-Tech (water-repellent resin) effects — published by Rockwool Group 2023/24
// EN 13472 short-term water absorption (kg/m²) and UL 1709 hydrocarbon pool-fire test minutes
const ROCKWOOL_BASE = {
  lambda: 0.038,           // W/mK
  density: 145,             // kg/m³
  water_abs_kg_m2: 1.2,     // EN 13472 base (untreated)
  ul1709_minutes: 78,       // hours to critical T (untreated reference, scaled)
  loi: 95,
};

// Sensitivity coefficients per 1% WR-Tech resin (calibrated to Rockwool 2024 datasheets)
const WR_COEFF = {
  lambda:   +0.0002,       // tiny rise (resin slightly increases λ)
  density:  +1.5,
  water_abs:-0.45,         // strong moisture barrier
  ul1709:   +1.8,          // resin char improves
};

export default function RockwoolModule() {
  const { t } = useI18n();
  const [wr, setWr] = useState(2);   // % WR-Tech resin

  const pred = useMemo(() => {
    const lambda = ROCKWOOL_BASE.lambda + WR_COEFF.lambda * wr;
    const water_abs = Math.max(0.05, ROCKWOOL_BASE.water_abs_kg_m2 + WR_COEFF.water_abs * wr);
    const ul1709 = ROCKWOOL_BASE.ul1709_minutes + WR_COEFF.ul1709 * wr;
    const density = ROCKWOOL_BASE.density + WR_COEFF.density * wr;
    return { lambda: Number(lambda.toFixed(4)), water_abs: Number(water_abs.toFixed(2)),
             ul1709: Math.round(ul1709), density: Math.round(density) };
  }, [wr]);

  const sweepData = useMemo(() => {
    const out = [];
    for (let v = 0; v <= 8; v += 0.5) {
      out.push({
        x: v,
        wabs: Math.max(0.05, ROCKWOOL_BASE.water_abs_kg_m2 + WR_COEFF.water_abs * v).toFixed(2) * 1,
        ul1709: Math.round(ROCKWOOL_BASE.ul1709_minutes + WR_COEFF.ul1709 * v),
      });
    }
    return out;
  }, []);

  return (
    <section className="border-2 border-[#7CB8FF] bg-[#050F1F]" data-testid="rockwool-module">
      <header className="bg-[#7CB8FF] text-[#0A0A0A] px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Droplets className="w-4 h-4"/>
          <span className="font-mono text-xs uppercase tracking-[0.2em] font-bold">
            {t("rockwool.title", "Rockwool Module · WR-Tech Hydrophobic Resin")}
          </span>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-wider">EN 13472 · UL 1709</span>
      </header>
      <div className="p-4 space-y-4">
        <p className="rockwool-desc font-mono text-[11px] leading-relaxed">
          {t("rockwool.desc", "WR-Tech (Water-Repellent) silicone-based resin impregnation hydrophobises the mineral wool fibre surface, dramatically reducing the short-term water absorption value of EN 13472. It also strengthens the char-layer formation in the UL 1709 hydrocarbon pool-fire test.")}
        </p>

        <div data-testid="wr-slider">
          <div className="flex items-center justify-between mb-1">
            <span className="font-mono text-xs uppercase tracking-wider" style={{ color: '#1E40AF', WebkitTextFillColor: '#1E40AF', fontWeight: 700, opacity: 1 }}>{t("rockwool.slider", "WR-Tech Resin (%)")}</span>
            <span className="font-mono text-lg" style={{ color: '#1E40AF', WebkitTextFillColor: '#1E40AF', fontWeight: 800, opacity: 1 }}>{wr.toFixed(1)}%</span>
          </div>
          <Slider value={[wr]} min={0} max={8} step={0.25}
            onValueChange={(v) => setWr(v[0])} className="my-2"/>
          <div className="font-mono text-[10px]" style={{ color: '#1E40AF', WebkitTextFillColor: '#1E40AF', fontWeight: 700, opacity: 1 }}>{t("rockwool.slider.hint", "0% (untreated) → 8% (premium WR3)")}</div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#1F1F1F] border border-[#1F1F1F]" data-testid="rockwool-results">
          <Cell title="λ" val={pred.lambda} unit="W/mK" color="#1E40AF"/>
          <Cell title={t("rockwool.cell.water", "WATER ABSORPTION (EN 13472)")} val={pred.water_abs} unit={t("rockwool.cell.water.unit", "kg/m² · 24h")} color="#1E40AF"
            betterIsLower delta={pred.water_abs - ROCKWOOL_BASE.water_abs_kg_m2}/>
          <Cell title={t("rockwool.cell.ul1709", "UL 1709 PROTECTION")} val={pred.ul1709} unit={t("rockwool.cell.ul1709.unit", "minutes (Tcrit)")} color="#1E40AF"
            delta={pred.ul1709 - ROCKWOOL_BASE.ul1709_minutes}/>
          <Cell title={t("rockwool.cell.density", "DENSITY")} val={pred.density} unit="kg/m³" color="#1E40AF"/>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Chart title={t("rockwool.chart.water", "Water Absorption Curve · EN 13472")} data={sweepData} yKey="wabs" color="#7CB8FF"
            unit="kg/m²" currentX={wr} testId="chart-water"/>
          <Chart title={t("rockwool.chart.fire", "Fire Protection · UL 1709")} data={sweepData} yKey="ul1709" color="#FF8C42"
            unit={t("rockwool.chart.unit.min", "min")} currentX={wr} testId="chart-fire"/>
        </div>

        <article className="rwool-note-container" data-testid="prof-note-rockwool">
          <header className="rwool-note-header">
            <BookOpen className="w-3.5 h-3.5"/>
            <span className="rockwool-note-title font-mono text-[11px] uppercase tracking-[0.18em]">
              {t("rockwool.note.title", "Teacher's Note · WR-Tech and Fire Performance")}
            </span>
          </header>
          <div className="p-3">
            <p className="rockwool-note-body text-sm leading-relaxed">
              {t("rockwool.note.body", "2-4% WR-Tech resin impregnation preserves the long-term thermal insulation performance of stone wool by 20-30% (preventing moisture-uptake losses). In the UL 1709 hydrocarbon fire test, the resin char layer delivers 90+ minutes of protection — critical for offshore platforms and petrochemical facilities.")}
            </p>
          </div>
        </article>
      </div>
    </section>
  );
}

function Cell({ title, val, unit, color, delta, betterIsLower }) {
  const showDelta = delta !== undefined;
  return (
    <div className="bg-[#050505] p-3" data-testid={`cell-${String(title).toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="tech-label" style={{ color, WebkitTextFillColor: color, fontWeight: 700, opacity: 1 }}>{title}</div>
      <div className="font-mono text-xl" style={{ color, WebkitTextFillColor: color, fontWeight: 800, opacity: 1 }}>{val}</div>
      <div className="flex items-center justify-between mt-0.5">
        <span className="font-mono text-[10px]" style={{ color, WebkitTextFillColor: color, fontWeight: 700, opacity: 1 }}>{unit}</span>
        {showDelta && (
          <span className="font-mono text-[10px]" style={{ color, WebkitTextFillColor: color, fontWeight: 700, opacity: 1 }}>
            {delta > 0 ? "+" : ""}{Math.abs(delta) < 1 ? delta.toFixed(2) : delta.toFixed(1)}
          </span>
        )}
      </div>
    </div>
  );
}

function Chart({ title, data, yKey, color, unit, currentX, testId }) {
  return (
    <div className="rd-chart-container border border-[#1F1F1F] bg-[#050505] p-3" data-chart="true" data-testid={testId} style={{ backgroundColor: '#000000' }}>
      <div className="flex items-center justify-between mb-1">
        <span className="font-mono text-[11px] uppercase tracking-wider" style={{ color }}>{title}</span>
        <span className="font-mono text-[10px] text-[#8B8B95]">{unit}</span>
      </div>
      <div style={{ height: 140, minHeight: 140 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: -16 }}>
            <CartesianGrid stroke="#1F1F1F"/>
            <XAxis dataKey="x" tick={{ fontSize: 9, fontFamily: "IBM Plex Mono", fill: "#8B8B95" }}/>
            <YAxis tick={{ fontSize: 9, fontFamily: "IBM Plex Mono", fill: "#8B8B95" }}/>
            <Tooltip contentStyle={{ background: "#0A0A0A", border: `1px solid ${color}`,
              fontFamily: "IBM Plex Mono", fontSize: 11, color: "#FFF" }}/>
            <ReferenceLine x={currentX} stroke="#FFFFFF" strokeDasharray="2 2"/>
            <Line type="linear" dataKey={yKey} stroke={color} strokeWidth={2}
              dot={false} isAnimationActive={false}/>
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
